import type { DateRange } from "@/stores/filterStore";

// Narrower than DraftRange below: every resolver here always produces
// concrete bounds, never the null/undrawn state a draft picker can be in.
interface ResolvedRange {
  start: Date;
  end: Date;
}

// The Date-based shape a draft picker's UI state uses — distinct from the
// store's own DateRange (ISO strings; see filterStore.ts), which exists so
// FilterStore$ only ever holds serializable data. Callers convert at the
// store boundary with parseISODate/toISODate below.
export interface DraftRange {
  start: Date | null;
  end: Date | null;
}

// Anchored at UTC noon, not midnight: PrimeReact's Calendar reads dates back
// via local getters (getDate/getMonth/getFullYear), not UTC ones. A
// UTC-midnight instant falls on the *previous* local calendar day for every
// negative-UTC-offset viewer (all of the Americas) — noon stays on the
// correct local day for every real-world timezone this app's users are in.
export const parseISODate = (value: string): Date =>
  new Date(`${value.slice(0, 10)}T12:00:00Z`);

export const toISODate = (date: Date): string =>
  date.toISOString().slice(0, 10);

export type QuickSelectKey =
  | "lastFourteenDays"
  | "lastMonth"
  | "lastNinetyDays"
  | "lastQuarter"
  | "lastSevenDays"
  | "lastThirtyDays"
  | "thisMonth"
  | "thisQuarter"
  | "yesterday";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// All four anchor at UTC noon (hour 12), same reasoning as parseISODate
// above — kept these at noon rather than midnight so every quick-select
// lands on the correct local calendar day too.
const startOfUTCDay = (date: Date): Date =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12)
  );

const startOfUTCMonth = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));

const endOfUTCMonth = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12));

const startOfUTCQuarter = (date: Date): Date => {
  const quarterMonth = Math.floor(date.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(date.getUTCFullYear(), quarterMonth, 1, 12));
};

const endOfUTCQuarter = (date: Date): Date => {
  const quarterMonth = Math.floor(date.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(date.getUTCFullYear(), quarterMonth + 3, 0, 12));
};

// Data freshness is T-1 UTC (see the PRD): "today" never has data yet, so
// every quick-select anchors to yesterday rather than now — matching the
// calendar's own maxDate, which disables today and beyond.
export const getYesterday = (now: Date): Date =>
  new Date(startOfUTCDay(now).getTime() - MS_PER_DAY);

// Earliest selectable day: the same calendar date one year before yesterday.
// The day is clamped to the target month's length so a Feb 29 yesterday maps
// to Feb 28 rather than overflowing into March. Anchored at UTC noon like
// every other bound here.
export const getMinSelectableDate = (now: Date): Date => {
  const yesterday = getYesterday(now);
  const year = yesterday.getUTCFullYear() - 1;
  const month = yesterday.getUTCMonth();
  const lastDayOfMonth = new Date(
    Date.UTC(year, month + 1, 0, 12)
  ).getUTCDate();
  return new Date(
    Date.UTC(year, month, Math.min(yesterday.getUTCDate(), lastDayOfMonth), 12)
  );
};

const rollingWindow = (now: Date, days: number): ResolvedRange => {
  const end = getYesterday(now);
  return { start: new Date(end.getTime() - (days - 1) * MS_PER_DAY), end };
};

export const QUICK_SELECT_RESOLVERS: Record<
  QuickSelectKey,
  (now: Date) => ResolvedRange
> = {
  lastFourteenDays: (now) => rollingWindow(now, 14),
  // Wholly in the past relative to yesterday, so unlike thisMonth/thisQuarter
  // below, this never needs clamping against the T-1 cutoff.
  lastMonth: (now) => {
    const lastDayOfPriorMonth = new Date(
      startOfUTCMonth(getYesterday(now)).getTime() - MS_PER_DAY
    );
    return {
      start: startOfUTCMonth(lastDayOfPriorMonth),
      end: endOfUTCMonth(lastDayOfPriorMonth),
    };
  },
  lastNinetyDays: (now) => rollingWindow(now, 90),
  lastQuarter: (now) => {
    const lastDayOfPriorQuarter = new Date(
      startOfUTCQuarter(getYesterday(now)).getTime() - MS_PER_DAY
    );
    return {
      start: startOfUTCQuarter(lastDayOfPriorQuarter),
      end: endOfUTCQuarter(lastDayOfPriorQuarter),
    };
  },
  lastSevenDays: (now) => rollingWindow(now, 7),
  lastThirtyDays: (now) => rollingWindow(now, 30),
  // Anchored to now (not yesterday) for the period start: on the 1st of a
  // new month/quarter, yesterday is the last day of the prior period, so
  // anchoring to yesterday would silently return last period's data.
  // The end is clamped to yesterday (T-1). On the very first day of a new
  // period, start > end — canApply blocks apply rather than sending a
  // wrong single-day request into the prior period.
  thisMonth: (now) => ({
    start: startOfUTCMonth(now),
    end: getYesterday(now),
  }),
  thisQuarter: (now) => ({
    start: startOfUTCQuarter(now),
    end: getYesterday(now),
  }),
  yesterday: (now) => {
    const yesterday = getYesterday(now);
    return { start: yesterday, end: yesterday };
  },
};

const RANGE_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const RANGE_LABEL_FORMATTER_WITH_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

// Both bounds are UTC-noon instants (see startOfUTCDay above), so
// formatting must pin to UTC too — otherwise a viewer behind UTC would see
// the day before the one actually selected.
export const formatDateRangeLabel = (range: DraftRange): string => {
  if (!range.start || !range.end) return "";
  if (toISODate(range.start) === toISODate(range.end)) {
    return RANGE_LABEL_FORMATTER_WITH_YEAR.format(range.end);
  }
  const sameYear = range.start.getUTCFullYear() === range.end.getUTCFullYear();
  const startLabel = sameYear
    ? RANGE_LABEL_FORMATTER.format(range.start)
    : RANGE_LABEL_FORMATTER_WITH_YEAR.format(range.start);
  return `${startLabel} – ${RANGE_LABEL_FORMATTER_WITH_YEAR.format(range.end)}`;
};

// Label for a range held in the store as ISO strings. The resolved range wins
// when it has both bounds — it is what the data on screen was fetched for;
// otherwise the picked range stands in until the page has resolved one.
export const formatStoredRangeLabel = (
  resolved: DateRange,
  picked: DateRange | null
): string => {
  const range = resolved.start && resolved.end ? resolved : picked;
  if (!range?.start || !range.end) return "";
  return formatDateRangeLabel({
    start: parseISODate(range.start),
    end: parseISODate(range.end),
  });
};

export const countDaysInclusive = (range: DraftRange): number => {
  if (!range.start || !range.end) return 0;
  return (
    Math.round((range.end.getTime() - range.start.getTime()) / MS_PER_DAY) + 1
  );
};
